import classNames from 'classnames';
import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import type { OnsiteLocation } from '../../../stores/types/video';
import { OnsiteLocationSessionRoute, SrtStreamStatus } from '../../../stores/types/video';
import { LOCATION_PUBLISHING_ROUTES } from '../../../stores/reducers/video';
import { GreenroomState as GS } from '../../../stores/types/types';
import {
	EntryDropdown,
	EntryDropdownButton,
	EntryDropdownOption,
	EntryDropdownButtonText,
	EntryHeader,
	EntrySpan,
	EntryButtonRow,
	Button,
	ButtonLabel,
	SrtCredentialsPanel,
	SrtCredentialsInput,
	SrtCredentialsCopyAll,
} from './elements';
import { setOnsiteLocationSession } from '../../../stores/actions/video';
import Socket from '../../../connection/socket';
import { setModalState } from '../../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../../stores/reducers/modal';
import { UpdateOnsiteLocation } from '../../../connection/video';
import useClickAwayListener from '../../../hooks/use-click-away-listener';
import { SRT_STREAM_URL } from '../../meta-menu/meta-onsite-location';
import { showAlert } from '../../general-components/alert-service';

interface Props {
	onsiteLocation: OnsiteLocation;
	socket: Socket | null;
	handleOpenCanvas: () => void;
}

export default function OnsiteLocationEntry(props: Props) {
	const {
		onsiteLocation,
		socket,
		handleOpenCanvas,
	} = props;

	const {
		id,
		locationName,
		pathName,
	} = onsiteLocation;

	const dispatch = useDispatch();

	const session = useSelector((s: GS) => s.SessionReducer.session);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const srtStreams = useSelector((s: GS) => s.VideoReducer.srtStreams);
	const livekitIngresses = useSelector((s: GS) => s.LivekitReducer.livekitIngresses);
	const {
		use_srt_streams: useSrtStreams,
		use_whip_streams: useWhipStreams,
	} = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const [locationDropdownOpen, setLocationDropdownOpen] = useState<boolean>(false);
	const [connectionDropdownOpen, setConnectionDropdownOpen] = useState<boolean>(false);
	const [showSrtCredentials, setShowSrtCredentials] = useState<boolean>(false);

	const locationDropdownRef = useRef<HTMLDivElement>(null);
	const connectionDropdownRef = useRef<HTMLDivElement>(null);
	const srtCredentialsRef = useRef<HTMLDivElement>(null);

	useClickAwayListener({
		ref: locationDropdownRef,
		onClickAway: () => null,
		onMousedownClickAway: () => setLocationDropdownOpen(false),
		open: locationDropdownOpen,
	});

	useClickAwayListener({
		ref: connectionDropdownRef,
		onClickAway: () => null,
		onMousedownClickAway: () => setConnectionDropdownOpen(false),
		open: connectionDropdownOpen,
	});

	useClickAwayListener({
		ref: srtCredentialsRef,
		onClickAway: () => null,
		onMousedownClickAway: () => setShowSrtCredentials(false),
		open: showSrtCredentials,
	});

	const otherExistingPublisher = useMemo(() => connectedUsers.some((user) => (
		user.session !== session
		&& user.onsiteLocationSession
		&& user.onsiteLocationSession.id === id
		&& LOCATION_PUBLISHING_ROUTES.includes(user.onsiteLocationSession.route)
	)), [connectedUsers, onsiteLocation, session]);

	const selectedSrtStream = useMemo(() => {
		if (!onsiteLocation?.srtId) return null;
		return srtStreams.find((stream) => stream.id === onsiteLocation.srtId);
	}, [srtStreams, onsiteLocation?.srtId]);

	const effectiveInputType = useMemo(() => {
		if (useWhipStreams && onsiteLocation?.inputType) return onsiteLocation.inputType;
		return onsiteLocation?.useSrt ? 'srt' : 'cam_mic';
	}, [useWhipStreams, onsiteLocation?.inputType, onsiteLocation?.useSrt]);

	const selectedWhipIngress = useMemo(() => {
		if (!onsiteLocation?.inputId || effectiveInputType !== 'whip') return null;
		return livekitIngresses.find((i) => i.id === onsiteLocation.inputId) ?? null;
	}, [livekitIngresses, onsiteLocation?.inputId, effectiveInputType]);

	const handleSelectLocation = (newLocation: OnsiteLocation) => {
		setLocationDropdownOpen(false);
		const url = new URL(window.location.href);
		window.history.replaceState(
			null,
			'',
			url.href.replace(pathName, newLocation.pathName),
		);
		dispatch(setOnsiteLocationSession({
			id: newLocation.id,
			route: OnsiteLocationSessionRoute.Entry,
		}));
	};

	const handlePublish = () => {
		dispatch(setOnsiteLocationSession({
			id,
			route: OnsiteLocationSessionRoute.Publishing,
		}));
	};

	const handlePublishAttempt = () => {
		if (otherExistingPublisher || selectedSrtStream?.status === SrtStreamStatus.active) {
			dispatch(setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.MULTI_ONSITE_LOCATION,
				modalData: {
					socket,
					onsiteLocationId: id,
					session,
					handlePublish,
				},
			}));
			return;
		}
		handlePublish();
	};

	const handleUpdateOnsiteLocation = (p: Partial<OnsiteLocation>) => {
		if (!token || !meeting) return;
		UpdateOnsiteLocation(
			token,
			meeting.meeting,
			id,
			p,
		);
	};

	const copyToClipboard = (value: string, type?: string) => {
		navigator.clipboard.writeText(value);
		showAlert({
			message: type ? 'Copied' : 'Link copied',
			description: type ? `${type} is attached to your clipboard` : 'Onsite URL is attached to your clipboard',
			type: 'general-notification',
			duration: 3000,
		});
	};

	const copyAllCredentials = () => {
		if (!selectedSrtStream) return;
		const streamId = `streamid=${selectedSrtStream.streamKey}`;
		const passphrase = `passphrase=${selectedSrtStream.srtPassphrase}`;
		const srtString = `${SRT_STREAM_URL}:6001?${streamId}&${passphrase}`;
		copyToClipboard(srtString, 'Full URL');
	};

	return (
		<>
			<EntryHeader>You are joining from:</EntryHeader>
			<div className="position-relative" ref={locationDropdownRef}>
				<EntryDropdownButton
					onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
					className={classNames({
						open: locationDropdownOpen,
						single: onsiteLocations.length === 1,
					})}
				>
					<EntryDropdownButtonText>
						{locationName}
					</EntryDropdownButtonText>
					<div className="flex-center">
						{onsiteLocations.length > 1 && (
							<Icon
								name={locationDropdownOpen ? ICONS.ACCORDION_UP : ICONS.ACCORDION_DOWN}
								size={24}
								color={COLORS.WHITE}
							/>
						)}
					</div>
				</EntryDropdownButton>
				<EntryDropdown className={classNames(locationDropdownOpen && 'open')}>
					<EntryDropdownOption className="header">
						<span className="bl-font-l3">CHANGE LOCATION</span>
					</EntryDropdownOption>
					{onsiteLocations.map((location, index) => (
						<EntryDropdownOption key={index} onMouseUp={() => handleSelectLocation(location)}>
							<span className="bl-font-p2 truncate-overflow">{location.locationName}</span>
							{id === location.id && (
								<div className="flex-center">
									<Icon name={ICONS.CHECK} size={10} color={COLORS.WHITE} />
								</div>
							)}
						</EntryDropdownOption>
					))}
				</EntryDropdown>
			</div>
			<EntrySpan>
				You can choose both options on the same computer, or use a second computer for each
			</EntrySpan>
			<EntryButtonRow>
				{(useSrtStreams || useWhipStreams) ? (
					<>
						<div className="position-relative" ref={connectionDropdownRef}>
							<Button
								onClick={() => setConnectionDropdownOpen(!connectionDropdownOpen)}
								className={classNames({ open: connectionDropdownOpen })}
							>
								<ButtonLabel className="grayscale-300">
									{effectiveInputType === 'whip' ? 'Using WHIP Stream' : effectiveInputType === 'srt' ? 'Using SRT Endpoint' : 'Add Cam & Mic'}
								</ButtonLabel>
								<Icon
									name={
										connectionDropdownOpen
											? ICONS.KEYBOARD_ARROW_UP
											: ICONS.KEYBOARD_ARROW_DOWN
									}
									size={12}
									color={COLORS.WHITE}
								/>
							</Button>
							<EntryDropdown className={classNames('connection', connectionDropdownOpen && 'open')} style={{ width: 'auto', minWidth: '100%' }}>
								<EntryDropdownOption onMouseUp={() => {
									handleUpdateOnsiteLocation(useWhipStreams
										? { inputType: 'cam_mic', inputId: null }
										: { useSrt: false });
									setConnectionDropdownOpen(false);
									handlePublishAttempt();
								}}>
									<span className="bl-font-p2">Local Cam & Mic</span>
									{effectiveInputType === 'cam_mic' && (
										<div className="flex-center">
											<Icon name={ICONS.CHECK} size={10} color={COLORS.WHITE} />
										</div>
									)}
								</EntryDropdownOption>
								{useSrtStreams && (
									<EntryDropdownOption onMouseUp={() => {
										handleUpdateOnsiteLocation(useWhipStreams
											? { inputType: 'srt', inputId: onsiteLocation.srtId ? String(onsiteLocation.srtId) : null }
											: { useSrt: true });
										setConnectionDropdownOpen(false);
									}}>
										<span className="bl-font-p2">SRT Endpoint</span>
										{effectiveInputType === 'srt' && (
											<div className="flex-center">
												<Icon name={ICONS.CHECK} size={10} color={COLORS.WHITE} />
											</div>
										)}
									</EntryDropdownOption>
								)}
								{useWhipStreams && (
									<EntryDropdownOption onMouseUp={() => {
										handleUpdateOnsiteLocation({ inputType: 'whip' });
										setConnectionDropdownOpen(false);
									}}>
										<span className="bl-font-p2">WHIP Stream</span>
										{effectiveInputType === 'whip' && (
											<div className="flex-center">
												<Icon name={ICONS.CHECK} size={10} color={COLORS.WHITE} />
											</div>
										)}
									</EntryDropdownOption>
								)}
							</EntryDropdown>
						</div>
						{effectiveInputType === 'whip' && useWhipStreams && (
							<div className="position-relative" ref={srtCredentialsRef}>
								<Button onClick={() => setShowSrtCredentials(!showSrtCredentials)}>
									<ButtonLabel className="grayscale-200">Show WHIP Credentials</ButtonLabel>
								</Button>
								{showSrtCredentials && (
									<SrtCredentialsPanel className="open">
										{selectedWhipIngress ? (
											<>
												<SrtCredentialsInput>
													<label>ENDPOINT</label>
													<div className="credential">
														<span>{selectedWhipIngress.endpoint}</span>
														<button
															className="copy-button"
															onClick={() => copyToClipboard(selectedWhipIngress.endpoint, 'Endpoint')}
														>
															<Icon name={ICONS.TEMPLATES} size={12} color={COLORS.TEXT_GRAY_300} />
														</button>
													</div>
												</SrtCredentialsInput>
												{selectedWhipIngress.streamKey && (
													<SrtCredentialsInput>
														<label>STREAM KEY</label>
														<div className="credential">
															<span>{selectedWhipIngress.streamKey}</span>
															<button
																className="copy-button"
																onClick={() => copyToClipboard(selectedWhipIngress.streamKey!, 'Stream Key')}
															>
																<Icon name={ICONS.TEMPLATES} size={12} color={COLORS.TEXT_GRAY_300} />
															</button>
														</div>
													</SrtCredentialsInput>
												)}
											</>
										) : (
											<SrtCredentialsInput>
												<label>No WHIP ingress assigned — contact your producer.</label>
											</SrtCredentialsInput>
										)}
									</SrtCredentialsPanel>
								)}
							</div>
						)}
						{effectiveInputType === 'srt' && (
							<div className="position-relative" ref={srtCredentialsRef}>
								<Button onClick={() => setShowSrtCredentials(!showSrtCredentials)}>
									<ButtonLabel className="grayscale-200">Show SRT Credentials</ButtonLabel>
								</Button>
								{selectedSrtStream && (
									<SrtCredentialsPanel className={classNames(showSrtCredentials && 'open')}>
										<SrtCredentialsInput>
											<label>URL</label>
											<div className="credential">
												<span>{`${SRT_STREAM_URL}:6001`}</span>
												<button
													className="copy-button"
													onClick={() => copyToClipboard(`${SRT_STREAM_URL}:6001`, 'URL')}
												>
													<Icon name={ICONS.TEMPLATES} size={12} color={COLORS.TEXT_GRAY_300} />
												</button>
											</div>
										</SrtCredentialsInput>
										<SrtCredentialsInput>
											<label>STREAM ID</label>
											<div className="credential">
												<span>{selectedSrtStream.streamKey}</span>
												<button
													className="copy-button"
													onClick={() => copyToClipboard(selectedSrtStream.streamKey, 'Stream ID')}
												>
													<Icon name={ICONS.TEMPLATES} size={12} color={COLORS.TEXT_GRAY_300} />
												</button>
											</div>
										</SrtCredentialsInput>
										<SrtCredentialsInput>
											<label>PASS PHRASE</label>
											<div className="credential">
												<span>{selectedSrtStream.srtPassphrase}</span>
												<button
													className="copy-button"
													onClick={() => copyToClipboard(selectedSrtStream.srtPassphrase, 'Pass Phrase')}
												>
													<Icon name={ICONS.TEMPLATES} size={12} color={COLORS.TEXT_GRAY_300} />
												</button>
											</div>
										</SrtCredentialsInput>
										<SrtCredentialsCopyAll>
											<button className="copy-all-button" onClick={copyAllCredentials}>
												Copy all
											</button>
										</SrtCredentialsCopyAll>
									</SrtCredentialsPanel>
								)}
							</div>
						)}
					</>
				) : (
					<Button onClick={handlePublishAttempt}>
						<Icon name={ICONS.CAMERA_ON} size={16} color={COLORS.TEXT_GRAY_200} />
						<ButtonLabel className="grayscale-200">Add Cam & Mic</ButtonLabel>
					</Button>
				)}
				<Button className="gradient" onClick={handleOpenCanvas}>
					<ButtonLabel className="grayscale-1200">Open Canvas in new window</ButtonLabel>
					<Icon name={ICONS.EXPAND} size={12} color={COLORS.TEXT_GRAY_1200} />
				</Button>
			</EntryButtonRow>
		</>
	);
}
